/**
 * UI 관련 유틸리티 함수
 * - 토스트 메시지 표시
 * - 로딩 인디케이터
 * - UI 엘리먼트 업데이트
 */

/**
 * 토스트 메시지를 표시하는 함수
 * @param {string} message - 표시할 메시지
 * @param {string} type - 메시지 유형 (success, error, warning, info)
 * @param {number} duration - 표시 지속 시간 (ms)
 */
export const showToast = (message, type = 'info', duration = 3000) => {
  // 이미 존재하는 토스트 컨테이너를 찾거나 새로 생성
  let toastContainer = document.querySelector('.toast-container');
  
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'toast-container position-fixed bottom-0 end-0 p-3';
    document.body.appendChild(toastContainer);
  }
  
  // 유형에 따른 클래스와 아이콘 설정
  let bgClass, icon;
  switch (type) {
    case 'success':
      bgClass = 'bg-success text-white';
      icon = 'bi-check-circle';
      break;
    case 'error':
      bgClass = 'bg-danger text-white';
      icon = 'bi-exclamation-triangle';
      break;
    case 'warning':
      bgClass = 'bg-warning';
      icon = 'bi-exclamation-circle';
      break;
    default:
      bgClass = 'bg-info text-white';
      icon = 'bi-info-circle';
  }
  
  // 고유 ID 생성
  const toastId = `toast-${Date.now()}`;
  
  // 토스트 엘리먼트 생성
  const toastHtml = `
    <div id="${toastId}" class="toast" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="toast-header ${bgClass}">
        <i class="bi ${icon} me-2"></i>
        <strong class="me-auto">SNS</strong>
        <small>${new Date().toLocaleTimeString()}</small>
        <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
      <div class="toast-body">
        ${message}
      </div>
    </div>
  `;
  
  // 토스트 추가
  toastContainer.insertAdjacentHTML('beforeend', toastHtml);
  
  // 토스트 객체 생성 및 표시
  const toastElement = document.getElementById(toastId);
  const toastInstance = new bootstrap.Toast(toastElement, {
    autohide: true,
    delay: duration
  });
  
  toastInstance.show();
  
  // 지정된 시간 후 DOM에서 제거
  setTimeout(() => {
    if (toastElement && toastElement.parentNode) {
      toastElement.parentNode.removeChild(toastElement);
    }
  }, duration + 500); // 토스트 애니메이션이 끝날 시간을 고려
};

/**
 * 로딩 스피너를 표시하거나 숨기는 함수
 * @param {boolean} show - 표시 여부
 * @param {string} containerId - 로딩 스피너를 표시할 컨테이너 ID
 * @param {string} message - 로딩 메시지
 */
export const showLoading = (show, containerId, message = '로딩 중...') => {
  const container = document.getElementById(containerId);
  
  if (!container) {
    console.error(`지정된 컨테이너 ID(${containerId})가 존재하지 않습니다.`);
    return;
  }
  
  // 로딩 스피너 생성 또는 제거
  if (show) {
    // 이미 존재하는 로딩 스피너 확인
    const existingSpinner = container.querySelector('.loading-container');
    if (existingSpinner) return;
    
    // 로딩 스피너 HTML 생성
    const spinnerHtml = `
      <div class="loading-container text-center py-4">
        <div class="loading-spinner"></div>
        <p class="mt-2 text-muted">${message}</p>
      </div>
    `;
    
    // 컨테이너에 추가
    container.insertAdjacentHTML('beforeend', spinnerHtml);
  } else {
    // 로딩 스피너 제거
    const spinner = container.querySelector('.loading-container');
    if (spinner) {
      spinner.remove();
    }
  }
};

/**
 * UI 요소 가시성을 업데이트하는 함수
 * @param {string} selector - 대상 요소의 CSS 선택자
 * @param {boolean} visible - 표시 여부
 */
export const updateElementVisibility = (selector, visible) => {
  const element = document.querySelector(selector);
  
  if (!element) {
    console.error(`선택자 ${selector}에 해당하는 요소가 존재하지 않습니다.`);
    return;
  }
  
  if (visible) {
    element.classList.remove('d-none');
  } else {
    element.classList.add('d-none');
  }
};

/**
 * 요소의 내용을 업데이트하는 함수
 * @param {string} selector - 대상 요소의 CSS 선택자
 * @param {string} content - 설정할 내용
 * @param {string} property - 업데이트할 속성 (기본값: 'innerHTML')
 */
export const updateElementContent = (selector, content, property = 'innerHTML') => {
  const element = document.querySelector(selector);
  
  if (!element) {
    console.error(`선택자 ${selector}에 해당하는 요소가 존재하지 않습니다.`);
    return;
  }
  
  if (property === 'value') {
    element.value = content;
  } else if (property === 'text') {
    element.textContent = content;
  } else {
    element.innerHTML = content;
  }
};

/**
 * 폼 검증 오류 표시 함수
 * @param {string} inputId - 입력 필드 ID
 * @param {string} errorMessage - 오류 메시지
 * @param {boolean} isValid - 유효 여부
 */
export const showValidationFeedback = (inputId, errorMessage, isValid) => {
  const input = document.getElementById(inputId);
  
  if (!input) return;
  
  // 기존 피드백 요소 제거
  const existingFeedback = input.nextElementSibling;
  if (existingFeedback && existingFeedback.classList.contains('invalid-feedback')) {
    existingFeedback.remove();
  }
  
  // 입력 필드 스타일 업데이트
  input.classList.remove('is-valid', 'is-invalid');
  
  if (isValid === true) {
    input.classList.add('is-valid');
  } else if (isValid === false) {
    input.classList.add('is-invalid');
    
    // 오류 메시지 요소 생성
    const feedbackElement = document.createElement('div');
    feedbackElement.className = 'invalid-feedback';
    feedbackElement.textContent = errorMessage;
    
    // 입력 필드 뒤에 삽입
    input.parentNode.insertBefore(feedbackElement, input.nextSibling);
  }
};

/**
 * 날짜를 사람이 읽기 쉬운 형식으로 포맷팅하는 함수
 * @param {string} dateString - ISO 형식의 날짜 문자열
 * @returns {string} 포맷팅된 날짜 문자열
 */
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  // 오늘 날짜인 경우 시간 포맷
  if (diffDay === 0) {
    if (diffHour === 0) {
      if (diffMin === 0) {
        return '방금 전';
      }
      return `${diffMin}분 전`;
    }
    return `${diffHour}시간 전`;
  } 
  // 어제 날짜인 경우
  else if (diffDay === 1) {
    return '어제';
  }
  // 1주일 이내인 경우
  else if (diffDay < 7) {
    return `${diffDay}일 전`;
  }
  // 그 외에는 날짜 표시
  else {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}; 